import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n";

/**
 * Right-panel question content — shared by every diagnostic built on
 * the generic diagnostic engine (Next Presentation Readiness Check,
 * Executive Presence Communication Diagnostic).
 *
 * 2026-10-21 incident fix: selecting an answer used to call `onNext`
 * (then named `onSelect`) IMMEDIATELY and schedule the parent's
 * advance-to-next-question on its own `setTimeout`. Two option clicks
 * on the SAME question in quick succession (e.g. a visitor changing
 * their mind) scheduled two independent timers, both of which fired
 * and advanced the index — silently SKIPPING the next question
 * (never rendered, never answerable), which later made the backend
 * reject the completed run as missing a required answer. Root cause:
 * production incident, 2026-10-21, executive_presence_communication
 * run dgr_01a16007-e620-7ad0-aeec-b827f6960454 — `epc_md1` silently
 * skipped, 6 identical 400s on retry, lead lost.
 *
 * Fix: selecting an option now ONLY highlights it locally — it never
 * advances anything. Advancing is the ONE explicit, user-initiated
 * "Next" button below, which fires exactly once per click (guarded by
 * `advancingRef`, reset only when a NEW question mounts) and carries
 * the final chosen option/free-text to the parent in one call. There
 * is no timer anywhere in this component, so there is no longer any
 * window in which two user actions can produce two advances.
 */
export const QuestionPanel = ({ question, selectedOptionId, freeTextValue, canGoBack, onNext, onBack, nextLabel }) => {
  const t = useT();
  const [localOptionId, setLocalOptionId] = useState(selectedOptionId || null);
  const [draftText, setDraftText] = useState(freeTextValue || "");
  // Guards against a double-fire of the Next button (double-click/
  // double-tap) producing two calls to `onNext` for the same question
  // before the parent has had a chance to re-render with a new
  // `question` prop. Reset whenever a (new) question mounts.
  const advancingRef = useRef(false);

  useEffect(() => {
    setLocalOptionId(selectedOptionId || null);
    setDraftText(freeTextValue || "");
    advancingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.questionId]);

  const selectedOption = question.options.find((o) => o.optionId === localOptionId);
  const requiresText = !!selectedOption?.requiresText;
  // `selectedOption` (not just a truthy `localOptionId`) is required —
  // a stale `localOptionId` left over from the PREVIOUS question would
  // otherwise still read as "proceedable" here (testing_agent, iter
  // 180: double-clicking Next could render the next question for one
  // frame with the previous question's localOptionId still set before
  // this component's own state had a chance to reset, letting a
  // second click fire with that stale, invalid-for-this-question id).
  // Combined with `key={question.questionId}` on the parent's render
  // of this component (forces a full remount per question, so there
  // is no such frame at all), this closes the gap on two independent
  // layers.
  const canProceed = !!selectedOption && (!requiresText || draftText.trim().length > 0);

  // Ignores the 2nd+ click of a multi-click (`event.detail` is the
  // native click-count: 1 for a normal click, 2+ for a double/triple
  // click at the same screen coordinates). Testing found that after
  // Next advances and this component remounts for a taller/shorter
  // new question, the 2nd click of a `dblclick` on the OLD Next
  // position can land on the NEW question's now-shifted last option,
  // silently pre-selecting something the visitor never read ("ghost
  // pre-select", iteration_181). A real double-click's first click
  // already registers normally; only the redundant 2nd+ one is
  // dropped, so this has no effect on normal single-click use.
  const handlePick = (option, event) => {
    if (event?.detail > 1) return;
    setLocalOptionId(option.optionId);
    if (!option.requiresText) setDraftText("");
  };

  const handleNextClick = () => {
    if (!canProceed || advancingRef.current) return;
    advancingRef.current = true;
    onNext(localOptionId, requiresText ? draftText.trim() : null);
  };

  return (
    <div data-testid="readiness-question-card" className="relative">
      <p className="font-serif text-xl md:text-3xl text-charcoal leading-snug mb-8" data-testid="readiness-question-text">
        {question.text}
      </p>

      <div className="space-y-3">
        {question.options.map((option, i) => {
          const isSelected = localOptionId === option.optionId;
          return (
            <button
              key={option.optionId}
              type="button"
              data-testid={`readiness-option-${option.optionId}`}
              aria-pressed={isSelected}
              onClick={(e) => handlePick(option, e)}
              className={`w-full text-left rounded-xl border px-5 py-4 font-body text-sm md:text-base transition-colors duration-150 flex items-center gap-3 ${
                isSelected
                  ? "bg-terracotta border-terracotta text-paper"
                  : "bg-paper border-ink/15 text-ink hover:border-terracotta/50"
              }`}

            >
              <span
                className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-medium ${
                  isSelected ? "border-paper/60 text-paper" : "border-ink/25 text-ink-soft"
                }`}
              >
                {String.fromCharCode(65 + i)}
              </span>
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>

      {requiresText && (
        <div className="mt-4" data-testid="readiness-free-text-block">
          <textarea
            data-testid="readiness-free-text-input"
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder={t("diagnosticUi.free_text_placeholder")}
            rows={2}
            className="w-full rounded-lg px-4 py-3 font-body text-sm text-ink bg-paper-deep border border-ink/15"
          />
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-4">
        <button
          type="button"
          data-testid="readiness-back-button"
          onClick={onBack}
          disabled={!canGoBack}
          className="font-body text-sm text-ink-soft underline-offset-4 hover:text-terracotta hover:underline disabled:opacity-0 disabled:pointer-events-none transition-colors"
        >
          &larr; {t("diagnosticUi.back_label")}
        </button>
        <button
          type="button"
          data-testid="readiness-next-button"
          onClick={handleNextClick}
          disabled={!canProceed}
          className="px-8 py-3 rounded-full font-body text-sm font-medium bg-terracotta text-paper disabled:opacity-40 disabled:cursor-not-allowed hover:bg-terracotta-dark transition-colors shrink-0"
        >
          {nextLabel || t("diagnosticUi.next_label")}
        </button>
      </div>
    </div>
  );
};

export default QuestionPanel;
